import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { getSizeConfig, type BrandLoadingSize } from './sizes';
import vinkLogo from '@/assets/vinklogo.png';
import './BrandLoading.css';

export interface BrandLoadingProps {
  /** 尺寸预设 */
  size?: BrandLoadingSize;
  /** 动效类型 */
  variant?: 'pulse-glow' | 'shimmer' | 'pulse' | 'spin';
  /** 加载提示文字 */
  tip?: string;
  /** 自定义类名 */
  className?: string;
}

/**
 * BrandLoading 品牌统一加载组件
 *
 * 使用 Framer Motion 实现流光扫过效果
 * 支持 5 级尺寸：xs / sm / md / lg / xl
 */
const BrandLoading: React.FC<BrandLoadingProps> = ({
  size = 'md',
  variant = 'pulse-glow',
  tip,
  className = '',
}) => {
  const config = getSizeConfig(size);

  return (
    <div
      className={`brand-loading brand-loading--${size} ${className}`.trim()}
      role="status"
      aria-label={tip || '加载中'}
    >
      <div
        className="brand-loading__logo-wrapper"
        style={{
          width: config.width,
          height: config.height,
        }}
      >
        {/* Logo 图片 */}
        <img
          src={vinkLogo}
          alt="Vink"
          className="brand-loading__logo"
          style={{
            width: config.width,
            height: config.height,
          }}
        />

        {/* 脉动光晕效果 */}
        {variant === 'pulse-glow' && (
          <>
            {/* Logo 呼吸缩放 + 间歇旋转 */}
            <motion.div
              className="brand-loading__pulse-glow-logo"
              animate={{
                scale: [1, 1.05, 1],
                opacity: [0.85, 1, 0.85],
                rotate: [0, 0, 0, 360, 360], // 停顿 → 快转 → 停顿
              }}
              transition={{
                scale: {
                  duration: 0.9,
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
                opacity: {
                  duration: 0.9,
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
                rotate: {
                  duration: 4, // 4秒一个周期：2秒停 + 1秒转 + 1秒停
                  repeat: Infinity,
                  ease: [0.4, 0, 0.2, 1], // 缓入缓出
                  times: [0, 0.4, 0.5, 0.7, 1], // 0-40% 静止，50%-70% 旋转，剩余静止
                },
              }}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={vinkLogo}
                alt=""
                style={{
                  width: config.width,
                  height: config.height,
                  objectFit: 'contain',
                }}
              />
            </motion.div>
            {/* 光晕扩散层 */}
            <motion.div
              className="brand-loading__glow-ring"
              animate={{
                scale: [0.8, 1.4, 0.8],
                opacity: [0.6, 0, 0.6],
              }}
              transition={{
                duration: 0.9,
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
          </>
        )}

        {/* 流光效果遮罩 */}
        {variant === 'shimmer' && (
          <motion.div
            className="brand-loading__shimmer"
            animate={{
              x: ['-100%', '200%'],
            }}
            transition={{
              duration: config.shimmerDuration,
              repeat: Infinity,
              ease: 'linear',
            }}
            style={{
              willChange: 'transform',
            }}
          />
        )}

        {/* 脉冲效果 */}
        {variant === 'pulse' && (
          <motion.div
            className="brand-loading__pulse"
            animate={{
              opacity: [0.4, 1, 0.4],
              scale: [0.95, 1, 0.95],
            }}
            transition={{
              duration: config.shimmerDuration,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )}

        {/* 旋转效果 */}
        {variant === 'spin' && (
          <motion.div
            className="brand-loading__spin-ring"
            animate={{
              rotate: 360,
            }}
            transition={{
              duration: config.shimmerDuration,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        )}
      </div>

      {/* 提示文字 */}
      {tip && <span className="brand-loading__tip">{tip}</span>}
    </div>
  );
};

export default memo(BrandLoading);
